<template>
    <div>
        <ui-modal :title="modalTitle" ref="taskModal" :size="task.id ? 'large' : 'normal ui-modal-allow-overflow'" class="task-modal" :contain-focus="false" dismiss-on="close-button esc">
            <div v-if="!isLoading && !isDestroying" class="container-fluid" style="padding: 0 0 1rem;">

                <!-- Display the new job fields if we're an unsaved Job !-->
                <div v-if="!task.id" style="margin-top: 2rem; margin-bottom: 1rem;">
                    <div v-if="!this.defaultTaskAttributes.instance_id && !this.task.booking_id && this.showNewJobInstance">
                        <label>Vehicle</label>
                        <vehicle-picker
                            placeholder="Select vehicle..."
                            @input="(e) => { task.instance = e; task.instance_id = e.id }"
                            :allow-addition="false"
                            :disabled="isSaving"
                            :status="newJobInstanceStatus"
                        ></vehicle-picker>
                        <label>Job</label>
                    </div>
                    <promise-input
                        :context="taskTemplateContext"
                        :disabled="isSaving"
                        :show-title="false"
                        :can-destroy="false"
                        :can-cancel="false"
                        :has-costs="false"
                        :show-warranty-item-options="showWarrantyItemOptions"
                        :selected-template="defaultTemplate"
                        :value="task"
                        :additional-validator="(task) => (this.defaultTaskAttributes.instance_id || task.instance_id || task.booking_id|| !task.showNewJobInstance) ? true : false"
                        @save="saveNewTemplateTask"
                    ></promise-input>

                    <div v-if="canAllocateParts" style="margin-top: 1rem;">
                        <a href="#" @click="$refs.fitKitModal.open(task.instance_id)">Want to fit a kit?</a>
                    </div>
                </div>

                <!-- Show normal task fields !-->
                <div v-else class="row">
                    <div class="col" style=" padding-top: 1.5rem;">    

                        <!-- Title & Meta !-->
                        <div>
                            <ui-alert v-if="task.pending_complete_at && !task.completed_at" type="warning" :dismissible="false">
                                <strong>Awaiting Costs: </strong>This job needs parts, consumable or time cards entered.
                            </ui-alert>
                            <!-- Title !-->
                            <div :class="canEditTitle ? (isEditingTitle ? 'editable editing' : 'editable') : ''" style="padding-bottom: 0rem;">
                                <span @click="editTitle" v-show="!isEditingTitle" class="has-edit-label" style="font-size: 1.5rem; font-weight: 500;">
                                    {{ this.task.title }}
                                </span>
                                <div class="row align-items-center" v-show="isEditingTitle">
                                    <div class="col">
                                        <ui-textbox ref="titleControl" placeholder="Task Title" v-model="task.title"></ui-textbox>
                                    </div>
                                    <div class="col-3">
                                        <ui-button @click="save" :loading="isSaving" :disabled="isSaving">Save</ui-button>
                                        <ui-icon-button v-if="task.id" @click="discard"><span class="material-icons" style="vertical-align: middle;">close</span></ui-icon-button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="task.deal || task.instance || task.contact || task.booking"  style="font-size: .8rem; color: #999; margin-bottom: 1rem;">
                                <span>
                                    <dl>
                                        <dd @click.prevent="$root.navigateTo('vehicles/%s/overview', [task.instance_id])" v-if="task.instance">
                                                <span class="registration small">
                                                    {{ task.instance.vehicle.registration.formatted_vrm }}
                                                </span> 
                                                {{ task.instance.vehicle.file_as }}
                                        </dd>
                                        <dd @click.prevent="$root.navigateTo('contacts/%s', [task.contact_id])" v-if="task.contact">
                                            <span class="material-icons" style="vertical-align: middle; font-size: 1rem; line-height: normal">person</span> {{ task.contact.primary_display_as }}
                                        </dd>
                                        <dd @click.prevent="$root.navigateTo('deals/%s/edit', [task.deal_id])" v-if="task.deal">Deal #{{ task.deal.id }}</dd>
                                        <dd @click.prevent="$root.navigateTo('contacts/%s', [task.deal.contact_id])" v-if="task.deal">{{ task.deal.contact.primary_display_as }}</dd>
                                        <dd @click.prevent="$root.navigateTo('contacts/%s', [task.booking.contact_id])" v-if="task.booking">{{ task.booking.contact.primary_display_as }}</dd>
                                        <dd @click.prevent="$emit('show-booking', task)" v-if="task.booking">
                                            Booking #{{ task.booking_id }}
                                        </dd>
                                    </dl>
                                </span>
                            </div>

                            <!-- Task Description !-->
                            <div v-if="task.id" :class="canEditDescription ? (isEditingDescription ? 'editable editing' : 'editable') : ''" style="margin-bottom: 1rem;">
                                <div @click="editDescription" v-if="hasDescription && !isEditingDescription" style="font-size: 1rem;" v-html="task.description.split('\n').join('<br>')"></div>
                                <span @click="editDescription" v-else-if="!isEditingDescription && canEditDescription" style="padding: .5rem 0 0rem; color:#999; display: inline-block; background: #f5f5f5; padding: .5rem; border-radius: 3px;">
                                    <span class="material-icons" style="vertical-align: middle; margin-right: .25rem;">format_align_justify</span>
                                    <span>Add a more detailed description...</span>
                                </span> 
                                <div class="row align-items-center" v-show="isEditingDescription">
                                    <div class="col-12">
                                        <ui-textbox
                                            ref="descriptionInput"
                                            multi-line
                                            :rows="1"
                                            placeholder="Describe the task..."
                                            v-model="task.description"
                                        ></ui-textbox>
                                    </div>
                                    <div class="col-3">
                                        <ui-button @click="save" :loading="isSaving" :disabled="isSaving">Save</ui-button>
                                        <ui-icon-button @click="discard"><span class="material-icons" style="vertical-align: middle;">close</span></ui-icon-button>
                                    </div>
                                </div>
                            </div>

                            <!-- Child Jobs !-->
                            <child-task-pane
                                v-if="task.children && task.children.length > 0"
                                :task="task"
                                @saving="isSaving = true"
                                @error="isSaving = false"
                                @added="childUpdated"
                                @completed="childUpdated"
                                @destroyed="childUpdated"
                            ></child-task-pane>

                            <!-- Files !-->
                            <div class="mb-2 pb-3 mt-2 attachments-column">
                                <div class="row">
                                    <div v-for="doc in documents" :class="'col-3 attachment' + (doc.destroying ? ' show-destroying' : '')">
                                        <ui-icon-button
                                            icon="delete"
                                            class="delete-icon"
                                            size="mini"
                                            color="red"
                                            :loading="doc.destroying"
                                            @click="removeDocument(doc)"
                                        />
                                        <img
                                            @click="showDocument(doc)"
                                            style="max-width: 100%; border-radius: 5px;"
                                            :src="doc.preview_url"
                                        />
                                    </div>
                                    <template v-if="task.defect && task.defect.images">
                                    <div v-for="image in task.defect.images" :class="'col-3 attachment'">
                                        <img
                                            style="max-width: 100%; border-radius: 5px;"
                                            :src="image.preview_url"
                                            @click="showImage(image.preview_url)"
                                        />
                                    </div>
                                    </template>
                                    <template v-if="task.children" v-for="childTask in task.children">
                                        <template v-if="childTask.defect && childTask.defect.images">
                                        <div v-for="image in childTask.defect.images" :class="'col-3 attachment'">
                                            <img
                                                style="max-width: 100%; border-radius: 5px;"
                                                :src="image.preview_url"
                                                @click="showImage(image.preview_url)"
                                            />
                                        </div>
                                        </template>
                                    </template>
                                </div>

                                <hr class="mb-0 mt-3">
                            </div>

                            <!-- Meta !-->
                            <div v-if="task.id">
                                <div class="row">
                                    <div class="col-3">
                                        <span style="color:#999;">Status</span>
                                        <div style="height: 40px; margin-bottom: 1rem; font-size: 1rem;" class="row col align-items-center">
                                            <div class="truncate" style="max-width: 100%;">
                                                {{ task.status_text }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-3" v-show="task.assignee">
                                        <span style="color:#999;">Assigned To</span>
                                        <div style="height: 40px; margin-bottom: 1rem;" class="row col align-items-center">
                                            <div>
                                                <avatar :user="task.assignee"></avatar>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-3" v-show="task.team">
                                        <span style="color:#999;">Team</span>
                                        <div style="height: 40px; margin-bottom: 1rem;" class="row col align-items-center">
                                            <div>
                                                <span v-if="task.team" class="badge badge-secondary" :style="{ fontSize: '.8rem', backgroundColor: task.team.colour ? task.team.colour : '#ccc' }">
                                                    {{ task.team.name }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-3" v-show="task.due_at">
                                        <span style="color:#999;">Due</span>
                                        <div style="height: 40px; margin-bottom: 1rem; font-size: 1rem;" class="row col align-items-center">
                                            <div>
                                                {{ task.due_at | formatDate('DD/MM/YYYY h:mm') }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-3" v-show="isCompleted">
                                        <span style="color:#999;">Completed</span>
                                        <div style="height: 40px; margin-bottom: 1rem;" class="row col align-items-center">
                                            {{ task.completed_at | formatDate }}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="hasTimeCards || hasConsumables || hasExpenseItems || canHaveComments" class="row" style="margin-top: 2rem;">

                                <!-- Time Entries !-->
                                <div class="col-12 modal-card-container" v-if="hasTimeCards">
                                    <div class="modal-card-outter">
                                        <div class="modal-card-inner">
                                            <h5>Times</h5>
                                            <ui-progress-circular v-if="isLoadingTimeCard" :indeterminate="true" style="position: absolute; top: -6px; right: 24px;"></ui-progress-circular>

                                            <table class="table table-sm" style="margin-bottom: 0;">
                                                <thead>
                                                    <tr>
                                                        <th style="width: 100px;">Date</th>
                                                        <th>Team Member</th>
                                                        <th style="width: 100px;" class="text-right">Time</th>
                                                        <th style="width: 50px;"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                <template v-for="timeCard in task.time_cards">
                                                    <tr>
                                                        <td>{{ timeCard.card_date | shortDate }}</td>
                                                        <td>{{ timeCard.user.name }}</td>
                                                        <td class="text-right">{{ minutesToHours(timeCard.minutes, 2) }} hours</td>
                                                        <td class="text-right"><ui-icon-button v-if="canAddTimes" icon="clear" @click="destroyTimeCard(timeCard)" size="small"></ui-icon-button></td>
                                                    </tr>
                                                    <tr v-if="timeCard.description">
                                                        <td colspan="4">{{ timeCard.description }}</td>
                                                    </tr>
                                                </template>
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <td></td>
                                                        <td style="font-weight: bold;" class="text-right">Total</td>
                                                        <td style="font-weight: bold;" class="text-right">{{ minutesToHours(sumBy(task.time_cards, 'minutes')) }} hours</td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <!-- Parts -->
                                <div class="col-12 modal-card-container" v-if="task.part_transactions && task.part_transactions.length > 0">
                                    <div class="modal-card-outter">
                                        <div class="modal-card-inner">
                                            <h5>Parts</h5>
                                            <hr>
                                            <table class="table table-sm" style="margin-bottom: 0;">
                                                <tbody>
                                                    <tr v-for="transaction in task.part_transactions">
                                                        <td style="width: 50px;" class="text-center">{{ Math.abs(transaction.quantity) }}  x</td>
                                                        <td>
                                                            <div>{{ transaction.part.description }}</div>
                                                        </td>
                                                        <td style="width: 100px;" class="text-right">{{ (Number(transaction.unit_cost) * Math.abs(transaction.quantity)) | currency(2) }}</td>
                                                        <td style="width: 50px;" class="text-right">
                                                            <ui-icon-button icon="clear" @click="detachPartTransaction(transaction)" size="small"></ui-icon-button>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                                <!--<tfoot>
                                                    <tr>
                                                        <td></td>
                                                        <td style="font-weight: bold;" class="text-right">Total</td>
                                                        <td style="font-weight: bold" class="text-right">{{ totalExpenses | currency(2) }}</td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>!-->
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <!-- Consumables (Historic - Deprecated) !-->
                                <div class="col-12 modal-card-container" v-if="hasConsumables">
                                    <div class="modal-card-outter">
                                        <div class="modal-card-inner">
                                            <h5>Consumables</h5>
                                            <ui-progress-circular v-if="isLoadingConsumables" :indeterminate="true" style="position: absolute; top: -6px; right: 24px;"></ui-progress-circular>
                                            <table class="table table-sm" style="margin-bottom: 0;">
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th style="width: 50px;">Qty</th>
                                                        <th style="width: 50px;"></th>
                                                    </tr>
                                                </thead>
                                                <tr v-for="consumable in task.consumables">
                                                    <td style="width: 75%;">
                                                        {{ consumable.name }}
                                                        <div style="color:#999; font-size: .9rem">{{ consumable.expense_category.name }}</div>
                                                    </td>
                                                    <td>{{ consumable.pivot.quantity }} {{ consumable.units }}</td>
                                                    <td class="text-right">
                                                        <ui-icon-button icon="clear" v-if="canAddConsumable" @click="detachConsumable(consumable)" size="small"></ui-icon-button>
                                                    </td>
                                                </tr>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                <!-- Expenses -->
                                <div class="col-12 modal-card-container" v-if="hasExpenseItems">
                                    <div class="modal-card-outter">
                                        <div class="modal-card-inner">
                                            <h5>Expenses &amp; Costs</h5>
                                            <hr>
                                            <ui-progress-circular v-if="isLoadingExpenses" :indeterminate="true" style="position: absolute; top: -6px; right: 24px;"></ui-progress-circular>
                                            <div v-for="group in groupedExpenses" style="margin-bottom: 1rem;">
                                                <div v-if="group[0].expense" class="row">
                                                    <div class="col">
                                                        <h6>{{ group[0].expense.contact.primary_display_as }}</h6>
                                                    </div>
                                                    <div class="col text-right">
                                                        <div v-if="group[0].expense" style="color:#999; font-size: .9rem">
                                                            <a :href="'/money/expenses?expense.expense_number=' + group[0].expense.id">
                                                                {{ group[0].expense.id }} <span v-if="group[0].expense.id && group[0].expense.reference">/</span> {{ group[0].expense.reference }}
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                                <table class="table table-sm" style="margin-bottom: 0;">
                                                <tbody>
                                                    <tr v-for="expense in group">
                                                        <td style="width: 50px;" class="text-center">{{ expense.pivot.quantity }}  x</td>
                                                        <td>
                                                            <div>{{ expense.description }}</div>
                                                        </td>
                                                        <td style="width: 100px;" class="text-right">{{ (expense.unit_gross * expense.pivot.quantity) | currency(2) }}</td>
                                                        <td style="width: 50px;" class="text-right">
                                                            <ui-icon-button v-if="canAddExpenses" icon="clear" @click="detachExpenseItem(expense)" size="small"></ui-icon-button>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                                <!--<tfoot>
                                                    <tr>
                                                        <td></td>
                                                        <td style="font-weight: bold;" class="text-right">Total</td>
                                                        <td style="font-weight: bold" class="text-right">{{ totalExpenses | currency(2) }}</td>
                                                        <td></td>
                                                    </tr>
                                                </tfoot>!-->
                                            </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                               <!-- Comments !-->
                                <div v-if="canHaveComments" class="col-12">
                                    <h5>Comments</h5>
                                    <hr>

                                    <comments-pane 
                                        v-if="task.id" 
                                        type="task" 
                                        owner-type="task" 
                                        :owner-id="task.id"
                                    ></comments-pane>
                                </div>
                            </div>
                        </div>

                        <div v-if="task.created_by" style="color:#ccc; margin-top: 1rem; margin-bottom: 1rem;">
                            {{ task.category | ucwords }} task created by {{ task.created_by.name }}, {{ task.created_at | humanDayTime }}
                        </div>
                    </div>

                    <!-- Action Buttons !-->
                    <div v-if="task.id" class="col-3" style="background: #fafafa; padding-top: 1.5rem;">
                        <div v-if="task.id && canAdd">
                            <h5 v-show="canAdd" style="margin-bottom: 1rem;">Add</h5>
                            
                            <!-- Related Contact !-->
                            <contact-selector 
                                v-show="canAssociateContact"
                                ref="contactSelector" 
                                :contact="this.task.contact" 
                                @save="contactSelected" 
                                :is-loading="isSaving" 
                            ></contact-selector>

                            <!-- Vehicle Selector !-->
                            <vehicle-selector 
                                v-show="canEditVehicle"
                                ref="vehicleSelector"
                                :instance="task.instance" 
                                @save="instanceSelected" 
                                :is-loading="isSaving" 
                                :task-type="task.type"
                            ></vehicle-selector>

                            <!-- Expense Selector !-->
                            <expense-selector 
                                v-if="canAddExpenses"
                                ref="expenseSelector"
                                :task="task"
                                @attach-expense="attachExpenseItem"
                                @attach-part="attachPart"
                                @attached="i => { task.expense_items.push(i); }"
                            ></expense-selector>

                            <!-- Consumable Selector
                            <consumable-selector 
                                v-if="canAddConsumable"
                                ref="consumableSelector"
                                :task="task"
                                @attach="attachConsumable"
                            ></consumable-selector>!-->

                            <!-- File Input !-->
                            <file-selector
                                ref="FileSelector"
                                :task="task"
                                @updated="updated"
                            ></file-selector>

                            <!-- Time Card Input !-->
                            <time-card-input
                                v-if="canAddTimes"
                                ref="timesInput"
                                :task="task"
                                @save="createTimeCard"
                            ></time-card-input>

                            <!-- Team Selector !-->
                            <team-selector 
                                v-show="canEditTeam"
                                ref="teamSelector"
                                :team="task.team" 
                                @save="teamSelected" 
                                :is-loading="isSaving" 
                            ></team-selector>          
                                      
                            <!-- Due Selector !-->
                            <due-selector 
                                v-show="canEditDueDate"
                                ref="dueSelector"
                                :due-at="task.due_at" 
                                @save="dueSelected" 
                                :is-loading="isSaving" 
                            ></due-selector>
                        </div>

                        <div v-if="!isCompleted && task.id && task.booking_id > 0">
                            <h5 style="margin-bottom: 1rem; margin-top: 1rem;">Flag</h5>

                            <!-- Started !-->
                            <ui-button 
                                icon="money_off" 
                                @click="toggleWarrantyClaim"
                                :raised="true === task.is_warranty_claim ? true : false"
                                :color="true === task.is_warranty_claim ? 'red' : 'default'"
                                style="width: 100%; margin-bottom: .5rem; justify-content: left;" 
                            >{{ 'Warranty Job' }}</ui-button>
                        </div>

                        <div v-if="task.id">                            
                            <h5 style="margin-bottom: 1rem; margin-top: 1rem;">Actions</h5>

                            <!-- Complete !-->
                            <ui-button 
                                v-show="!isCompleted"
                                @click="showCompleteModal" 
                                icon="check" 
                                style="width: 100%; margin-bottom: .5rem; justify-content: left;" 
                            >Complete</ui-button>

                            <!-- Un-complete !-->
                            <ui-button
                                v-show="isCompleted"
                                @click="uncomplete"
                                icon="undo"
                                style="width: 100%; margin-bottom: .5rem; justify-content: left;"
                            >Un-complete</ui-button>

                            <!-- Assign a user !-->
                            <assign-selector 
                                v-show="canAssign"
                                ref="assignSelector" 
                                :user="this.task.assignee"
                                @save="taskAssigned" 
                                :is-loading="isSaving" 
                            ></assign-selector>

                            <!-- Delete !-->
                            <ui-button 
                                @click="destroy" 
                                icon="delete" 
                                style="width: 100%; margin-bottom: .5rem; justify-content: left;"
                            >Delete</ui-button>
                        </div>
                    </div>
                </div>

            </div>
            <div v-else style="padding: 2rem 0 1rem">
                <cog-loader></cog-loader>
            </div>
        </ui-modal>
        
        <!-- Image Lightbox !-->
        <lightbox ref="lightbox"></lightbox>

        <!-- Complete Task Modal !-->
        <ui-modal 
            ref="completeModal" 
            title="Complete Task"
            dismiss-on="close-button esc"
        >
            <p style="font-size: 1rem;">
                Have <strong>all</strong> the costs (parts, consumables and times) been added to this job?
            </p>
            <ui-button 
                :loading="isSaving"
                @click="complete(true)"
            >
                Yes
            </ui-button> 
            <ui-button 
                :disabled="isSaving"
                color="primary"
                @click="pendingComplete"
            >
                No, they'll be added later
            </ui-button>
        </ui-modal>

        <!-- Fit Kit Modal !-->
        <fit-kit-modal ref="fitKitModal" @fitted="kitFitted"></fit-kit-modal>
    </div>
</template>

<style lang="scss">
    .task-modal {
        &.ui-modal--size-large > .ui-modal__wrapper > .ui-modal__container {
            width: 64rem;
        }

        .attachments-column {

            .attachment {
                cursor: pointer;

                .ui-icon-button {
                    position: absolute;
                    top: 0; right: 0; 
                    transform: translateY(-50%);
                    display: none;
                }

                &:hover,
                &.show-destroying {
                    .ui-icon-button {
                        display: block;
                    }
                }
            }
        }

        .type-btn {
            display: inline-block;
            padding: 2rem;
            text-align: center;
            font-size: 1.5rem;
            cursor: pointer;

            span { 
                display: block;
                font-size: 4.3rem; }
        }
        .modal-card-container {
            margin-bottom: 1rem;

            & + .modal-card-container { 
                margin-top: -1rem; 
                .modal-card-outter { padding-top: 0; }
            }

            .modal-card-outter {
                box-shadow: 2px 2px 1px 1px rgba(0,0,0,0.05);
                margin-bottom: 1rem;
                border: 1px solid #eee;
                border-radius: 3px;

                .modal-card-inner { 
                    background: #fff; border-radius: 3px; padding: 1rem;

                    .table { margin-top: 0; }
                }
            }
        }
        // DRY DefaultRow.vue
        dl {
            margin: 0;

            dd { 
                display: inline-block;
                margin-bottom: 0;
                cursor: pointer;
                &:hover { color: #666; }
                &:after {
                    font-size: .5rem;
                    padding: 0 .5rem;
                    content: '\25A0';
                    vertical-align: center;
                }
                &:last-child:after {
                    display: none;
                }
            }
        }

        .editable {
            cursor: pointer;
            &.editing {
                display: block;
                padding: .5rem .5rem 1rem;
                margin: 0 -.5rem;
            }

            &:hover {
                //background: #f8f8f8;
                opacity: .3;
                .has-edit-label:after {
                    content: "Edit";
                    display: inline-block;
                    color: #ddd;
                    text-decoration: underline;
                    padding-left: .5rem;
                    font-size: 12px;
                }

                &.editing {
                    opacity: 1;
                    .has-edit-label:after { display: none; }
                }
            }

            .edit-link {
                color: #999; font-size: .8rem; 
                font-weight:normal; text-decoration: underline;
                visibility: hidden;
            }

            &:hover { 
                .edit-link {
                    visibility: visible; 
                }
            }
        }

        .ui-modal__body {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            padding-right: 1rem !important;
        }
    }
</style>

<script>
    import PromiseInput from '../forms/controls/PromiseInput'
    import AssignSelector from './controls/AssignSelector'
    import VehicleSelector from './tasks/VehicleSelector'
    import TeamSelector from './tasks/TeamSelector'
    import DueSelector from './tasks/DueSelector'
    import ContactSelector from './tasks/ContactSelector'
    import ExpenseSelector from './tasks/ExpenseSelector'
    import ConsumableSelector from './tasks/ConsumableSelector'
    import TimeCardInput from './tasks/TimeCardInput'
    import FileSelector from './tasks/FileSelector'
    import { TimeCardResource } from "../../classes/Api/TimeCard";
    import { TaskSchema, TaskResource, TaskTypes } from '../../classes/Api/Task'
    import { DocumentResource } from '../../classes/Api/Document'
    import { DealLifeCycleStatuses } from '../../classes/Api/Deal'
    import { VehicleLifeCycleStatuses } from '../../classes/Api/Vehicle'
    import Filters from '../../filters'
    import CommentsPane from '../comments/CommentsPane'
    import {groupBy, sum, sumBy} from 'lodash'
    import ChildTaskPane from "../tasks/modal/ChildTaskPane.vue";
    import Lightbox from "../elements/Lightbox.vue";
    import {minutesToHours} from "../../filters/date/minutesToHours";
    import Bus from "../../events";
    import DealerKit from "../../api";
    import {PartLedgerTransactionType} from "../../../../api-clients/ts";
    import FitKitModal from "../../../ts/Parts/FitKitModal.vue";

    const relations = [
        'assignee', 
        'team', 
        'instance', 
        'inspection',
        'createdBy', 
        'invoiceItems', 
        'contact', 
        'template',
        'expenseItems',
        'expenseItems.expense',
        'expenseItems.expense.contact',
        'consumables',
        'consumables.expenseCategory',
        'timeCards',
        'timeCards.user',
        'deal.contact',
        'booking',
        'booking.contact',
        'booking.instance',
        'documents',
        'children',
        'children.assignee',
        'defect',
        'defect.images',
        'children.defect',
        'children.defect.images',
        'partTransactions',
        'partTransactions.part',
    ];

    export default {
        filters: Filters,
        components: {
            FitKitModal,
            Lightbox,
            ChildTaskPane,
            AssignSelector, 
            VehicleSelector, 
            TeamSelector, 
            DueSelector, 
            ContactSelector,
            ExpenseSelector,
            ConsumableSelector,
            TimeCardInput,
            CommentsPane,
            PromiseInput,
            FileSelector,
        },
        props: {
            defaultTemplate: {
                type: Object
            },
            taskTemplateContext: {
                type: String
            },
            softSave: {
                type: Boolean,
                default: false,
            },
            showWarrantyItemOptions: {
                type: Boolean,
                default: false,
            },
            closeOnNew: {
                type: Boolean,
                default: true,
            },
            allowVehicleEditing: {
                type: Boolean,
                default: true,
            },
            defaultTaskAttributes: {
                type: Object,
                default() {
                    return {}
                }
            },
            showNewJobInstance: {
                type: Boolean,
                default: true,
            },
        },
        data() {
            return {
                TaskTypes,
                task: new TaskSchema(),
                taskBeforeEdit: null,
                isEditingTitle: false,
                isEditingDescription: false,
                isLoading: false,
                isLoadingExpenses: false,
                isLoadingConsumables: false,
                isLoadingTimeCard: false,
                isSaving: false,
                isDestroying: false,
            }
        },
        methods: {
            sumBy,
            sum,
            minutesToHours,
            showImage(url) {
                this.$refs.lightbox.open(url)
            },
            showDocument(document) {
                Bus.emit('showLibraryDocument', { document })
            },
            removeDocument(doc) {
                doc.destroying = true
                DocumentResource.destroy(doc.uuid).then(r => {
                    doc.destroying = false
                    doc.destroyed = true
                })

                this.updated()
            },
            updated() {
                this.reload()

                setTimeout(() => {
                    this.$emit('updated', this.task)
                }, 250)
            },
            loadTask(task) {
                TaskResource.find(task.id, { with: relations}).then(response => {
                    response.data.documents = response.data.documents.map(d => {
                        d.destroying = false
                        d.destroyed = false
                        return d
                    })

                    this.task = response.data;

                    this.isLoading = false;
                }, e => {
                    this.$refs['taskModal'].close();
                    this.isLoading = false;
                    handleApiError(e)
                });
            },
            reload() {
                if (this.task.id) {
                    this.loadTask(this.task)
                }
            },
            kitFitted() {
                this.$emit('updated', this.task);
                this.close()
            },
            open(task = null) {
                this.task = new TaskSchema()
                this.taskBeforeEdit = null
                this.isEditingTitle = false
                this.isEditingDescription = false
                this.isLoading = false
                this.isLoadingExpenses = false
                this.isLoadingConsumables = false
                this.isLoadingTimeCard = false
                this.isSaving = false
                this.isDestroying = false

                if (task && task.id) {
                    this.isLoading = true;

                    this.$refs['taskModal'].open();

                    this.loadTask(task);
                } else {
                    let attr = Object.assign({
                        instance: null,
                        assignee: null,
                        team: null,
                    }, this.defaultTaskAttributes, task ? task : {})

                    this.task = new TaskSchema(attr)

                    this.$refs['taskModal'].open();

                    this.isEditingTitle = true
                }
            },
            close() {
                this.$refs['taskModal'].close();
            },
            toggleWarrantyClaim() {
                this.task.is_warranty_claim = !this.task.is_warranty_claim
                return this.save()
            },
            showCompleteModal() {
                // We only offer 'pending complete' on jobs not todos.
                if (TaskTypes.JOB === this.task.type) {
                    this.$refs.completeModal.open();
                } else {
                    this.complete();
                }
            },
            pendingComplete() {
                this.isSaving = true;
                TaskResource.pendingComplete(this.task.id, { with: relations}).then(response => {
                    this.task = response.data;
                    this.$refs.completeModal.close();
                    this.$refs['taskModal'].close();
                    this.$emit('updated', this.task);
                }, handleApiError);
            },
            complete(supressWarning = false) {
                let confirmMessage = 'Are you sure you want to COMPLETE this task?'

                if (this.task.children.filter(task => !task.completed_at).length > 0) {
                    confirmMessage = 'There are incomplete sub-jobs, completing the parent task will complete them, do you want to continue?'
                }

                if (supressWarning || confirm(confirmMessage)) {
                    this.isSaving = true;
                    TaskResource.complete(this.task.id, { with: relations}).then(response => {
                        this.task = response.data;
                        this.$refs.completeModal.close();
                        this.$refs['taskModal'].close();
                        this.$emit('updated', this.task);
                        this.isSaving = false
                    }, handleApiError);
                }
            },
            uncomplete() {
                if (confirm('Are you sure you want to un-complete this job?')) {
                    this.isSaving = true
                    TaskResource.uncomplete(this.task.id, { with: relations }).then(response => {
                        this.task = response.data;
                        this.$emit('updated', this.task);
                        this.isSaving = false
                    }, handleApiError)
                }
            },
            destroy() {
                if (confirm('Are you sure you want to delete this task?')) {
                    this.isDestroying = true;
                    TaskResource.destroy(this.task.id).then(response => {
                        this.$nextTick(() => {
                            this.$refs['taskModal'].close();
                            this.$emit('destroyed', this.task);
                        })
                    }, e => { this.isDestroying = true; handleApiError(e); });
                }
            },
            editTitle() {
                if (this.canEditTitle) {
                    this.taskBeforeEdit = Object.assign({}, this.task);
                    this.isEditingTitle = true;
                }
            },
            editDescription() {
                if (this.canEditDescription) {
                    this.taskBeforeEdit = Object.assign({}, this.task);
                    this.isEditingDescription = true;
                    this.$nextTick(() => {
                        this.$refs.descriptionInput.refreshSize();
                    })
                }
            },
            discard() {
                this.task = this.taskBeforeEdit;
                this.isEditingTitle = false;
                this.isEditingDescription = false;
            },
            saveNewTemplateTask(task, stayOpen = false) {
                this.task = Object.assign(task, {
                    type: this.task.type,
                    instance_id: this.task.instance_id
                })

                this.save().then(() => {
                    if (stayOpen) {
                        this.task = new TaskSchema(this.defaultTaskAttributes)
                    } else if (this.closeOnNew) {
                        this.close();
                    }
                })
            },
            childUpdated() {
                this.reload()
                this.finishSave()
            },
            save() {
                return new Promise((resolve, reject) => {
                    if (this.softSave) {
                        this.finishSave()
                        resolve();
                        return
                    }

                    this.isSaving = true;

                    let tasksToSave = [this.task]

                    // Save the task children if this is a new task.
                    if (!this.task.id && this.task.children) {
                        tasksToSave = this.task.children.map(
                            task => Object.assign({}, this.defaultTaskAttributes, task)
                        )
                    }

                    (async () => {
                        for (const task of tasksToSave) {
                           await TaskResource.storeOrUpdate(task.id, task, {with: relations}).then(response => {
                               resolve(response.data)
                           }, e => {
                               handleApiError(e, reject)
                           });
                        }
                    })().then(() => {
                        if (tasksToSave.length > 1) {
                            this.task.id = tasksToSave[0].id
                            this.task.children = tasksToSave
                            this.finishSave({data: this.task})
                        } else {
                            this.finishSave({data: this.task})
                        }
                    })
                });
            },
            finishSave(response = null) {
                this.isEditingTitle = false;
                this.isEditingDescription = false;
                this.isSaving = false;
                this.$emit('updated', this.task);
                if (response) {
                    this.task = Object.assign(this.task, response.data)
                }
            },
            contactSelected(contact) {
                if (contact) {
                    this.task.contact = contact
                    this.task.contact_id = contact.id
                } else {
                    this.task.contact = null
                    this.task.contact_id = null
                }

                this.save().then(response => {
                    this.$refs['contactSelector'].reset();

                    this.$emit('updated', this.task);
                });
            },
            taskAssigned(assignee = null) {
                if (assignee) {
                    this.task.assignee = assignee;
                    this.task.assignee_id = assignee.id;
                } else {
                    this.task.assignee = null;
                    this.task.assignee_type = null;
                    this.task.assignee_id = null;
                }

                this.save().then(response => {
                    this.$refs['assignSelector'].close();

                    this.$emit('updated', this.task);
                });
            },
            taskInspectionAssigned(inspection) {
                this.task.inspection_id = inspection ? inspection.id : null;
                this.task.inspection = inspection;

                this.save().then(response => {
                    this.$refs['inspectionSelector'].reset();

                    this.$emit('updated', this.task);
                });
            },
            instanceSelected(instance) {
                if (instance.id) {
                    this.task.instance = instance;
                    this.task.instance_id = instance.id;
                } else {
                    this.task.instance = null;
                    this.task.instance_id = null;
                }

                this.save().then(response => {
                    this.$refs['vehicleSelector'].reset();

                    this.$emit('updated', this.task);
                });
            },
            teamSelected(team) {
                if (team.id) {
                    this.task.team = team;
                    this.task.team_id = team.id;
                } else {
                    this.task.team = null;
                    this.task.team_id = null;
                }

                this.save().then(response => {
                    this.$refs['teamSelector'].reset();
                    
                    this.$emit('updated', this.task);
                });
            },
            dueSelected(date) {
                this.task.due_at = date;

                this.save().then(response => {
                    this.$refs['dueSelector'].reset();

                    this.$emit('updated', this.task);
                });
            },
            detachExpenseItem(item) {
                if (confirm('Are you sure you want to remove this expense from the task?')) {
                    this.isLoadingExpenses++;
                    TaskResource.detachExpenseItem(this.task.id, item.id).then(response => {
                        this.task.expense_items = this.task.expense_items.filter(existing => {
                            return existing.id !== item.id
                        })
                        this.isLoadingExpenses--

                        this.$refs.expenseSelector.invalidateLoadedItems()
                    }, e => { this.isLoadingExpenses--; handleApiError(e) });
                }
            },
            detachPartTransaction(transaction) {
                DealerKit.parts.destroyInventoryTransaction(transaction.part.id, transaction.id).then(response => {
                    this.task.part_transactions = this.task.part_transactions.filter(existing => {
                        return existing.id !== transaction.id;
                    })
                }).catch(() => {
                    alert('Could not detach the part')
                })
            },
            attachPart(part) {
                DealerKit.parts.storeInventoryTransaction(part.id, [
                    {
                        type: PartLedgerTransactionType.Out,
                        quantity: 0 - part.quantity_selected,
                        job_id: this.task.id
                    }
                ]).then(response => {
                    response.data.data.forEach(item => {
                        // Transform the value so it mirrors that returned by the legacy task API
                        item.unit_cost_tax_rate = item.cost.tax_rate
                        item.unit_cost = item.cost.amount

                        this.task.part_transactions.push(item)
                    })
                }).catch(e => {
                    alert('We had trouble attaching the part to this job')
                    throw e
                })
            },
           attachExpenseItem(item) {
            this.isLoadingExpenses++;
                TaskResource.attachExpenseItem(this.task.id, item.id, item.quantity_selected).then(response => {
                    this.task = Object.assign(this.task, { expense_items: response.data.expense_items })
                    this.isLoadingExpenses--
                }, e => { this.isLoadingExpenses--; handleApiError(e) })
            },        
            detachConsumable(item) {
                if (confirm('Are you sure you want to remove this consumable from the task?')) {
                    this.isLoadingConsumables++;
                    TaskResource.detachConsumable(this.task.id, item.id).then(response => {
                        this.task.consumables = this.task.consumables.filter(existing => {
                            return existing.id !== item.id
                        })
                        this.isLoadingConsumables--
                    }, e => { this.isLoadingConsumables--; handleApiError(e) });
                }
            },
            attachConsumable(item) {
                this.isLoadingConsumables++;
                    TaskResource.attachConsumable(this.task.id, item.id, item.quantity_selected).then(response => {
                        this.task = Object.assign(this.task, { consumables: response.data.consumables })
                        this.isLoadingConsumables--
                    }).catch(e => { this.isLoadingConsumables--; handleApiError(e) })
            },
            createTimeCard(card) {
                this.isLoadingTimeCard = true;
                TimeCardResource.store({task_id: this.task.id, ...card}).then(response => {
                    this.task.time_cards.push(response.data);
                    this.isLoadingTimeCard = false;
                }).catch(e => { this.isLoadingTimeCard = false; handleApiError(e); });
            },
            destroyTimeCard(card) {
                if (confirm('Are you sure you want to remove this time card?')) {
                    this.isLoadingTimeCard++

                    TimeCardResource.destroy(card.id).then(response => {
                        this.task.time_cards = this.task.time_cards.filter(item => {
                            return item.id !== card.id
                        })

                        this.isLoadingTimeCard--
                    }, e => { this.isLoadingTimeCard--; handleApiError(e); });
                }
            }
        },   
        computed: {
            canAllocateParts() {
                return window.marque.ui.features.inventory && this.$store.getters['user/can']('allocate.inventory')
            },
            groupedExpenses() {
                  return groupBy(this.task.expense_items, i => {
                      return i.expense_id
                  })
            },
            documents() {
                return this.task.documents.filter(d => {
                    return !d.destroyed
                })
            },
            newJobInstanceStatus() {
                return [
                    VehicleLifeCycleStatuses.IN_STOCK,
                    VehicleLifeCycleStatuses.DUE_IN,
                    VehicleLifeCycleStatuses.RESERVED,
                    VehicleLifeCycleStatuses.AWAITING_DELIVERY
                ]
            },
            modalTitle() {
                if (this.task.type === TaskTypes.JOB) {
                    return this.task.id ? 'Job' : 'New Job'
                }

                if (this.task.type === TaskTypes.TODO) {
                    return this.task.id ? 'Task' : 'New Task'
                }

                return this.isLoading ? '' : 'New Task'
            },
            totalExpenses() {
                return sum(this.task.expense_items.map(item => {
                    return item.unit_gross * item.pivot.quantity 
                }))
            },
            canHaveComments() {
                return !this.task.parent_id && this.task.id && !this.task.booking_id;
            },
            hasTimeCards() {
                return this.isLoadingTimeCard > 0 || this.task.time_cards && this.task.time_cards.length > 0;
            },
            hasConsumables() {
                return this.isLoadingConsumables > 0 || this.task.consumables && this.task.consumables.length > 0;
            },
            hasExpenseItems() {
                return this.isLoadingExpenses > 0 || this.task.expense_items && this.task.expense_items.length > 0;
            },
            isCompleted() {
                return this.task.completed_at ? true : false;
            },
            isPendingComplete() {
                return this.task.pending_complete_at ? true : false
            },
            isAccountableTask() {
                return TaskTypes.JOB === this.task.type
            },
            isCancelledDealTask() {
                return this.task.deal && (
                    DealLifeCycleStatuses.REFUNDED === this.task.deal.life_cycle_status ||
                    DealLifeCycleStatuses.CANCELLED === this.task.deal.life_cycle_status
                ) && !this.isCompleted
            },
            user() {
                return window.marque.ui.user;
            },
            hasDescription() {
                return this.task.description && this.task.description.length > 0;
            },
            canAdd() {
                return this.task && (
                    this.canAssociateContact ||
                    this.canEditVehicle ||
                    this.canAddConsumable ||
                    this.canAddTimes ||
                    this.canEditTeam ||
                    this.canEditDueDate
                )
            },
            canAssociateContact() {
                return TaskTypes.TODO === this.task.type && 
                    !this.isCompleted && !this.isCancelledDealTask && !this.task.deal_id;
            },
            canEditTitle() {
                return !this.isCompleted && 
                    !this.isCancelledDealJob && 
                    (!this.task.template || !this.task.template.prevent_title_modification) && !this.isPendingComplete
            },
            canEditDescription() {
                return !this.isCompleted && !this.isCancelledDealTask && !this.isPendingComplete;
            },
            canEditVehicle() {
                return !this.isCompleted && this.allowVehicleEditing && !this.task.deal_id && !this.isPendingComplete;
            },
            canAddConsumable() {
                return this.isAccountableTask && !this.isCompleted
            },
            canAddTimes() {
                return this.isAccountableTask && !this.isCompleted
            },
            canAddExpenses() {
                return this.isAccountableTask && !this.isCompleted
            },
            canEditDueDate() {
                return !this.isCompleted;
            },
            canEditTeam() {
                return !this.isCompleted && !this.isCancelledDealTask && 
                    (!this.task.template_id || !this.task.template.team_type)
            },
            canAssign() {
                return !this.isCompleted && !this.isCancelledDealTask && !this.isPendingComplete;
            },
            canComplete() {
                return !this.isCompleted;
            },
            canDelete() {
                return !this.task.deal_id;
            },
        },
    }
</script>